import { useState, useRef, useCallback } from 'react';
import type { ConversationItem } from '../../types';
import { getAirline } from '../../utils/airlines';
import { AvatarImg } from '../shared/Avatar';
import { hapticMedium } from '../../utils/haptics';
import { Trash2, LogOut } from 'lucide-react';

interface ConversationRowProps {
  item: ConversationItem;
  onClick: () => void;
  onDelete?: () => void;
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

const SWIPE_THRESHOLD = 60;
const DELETE_BTN_WIDTH = 80;

function useSwipeToDelete(onDelete?: () => void) {
  const startX = useRef(0);
  const currentX = useRef(0);
  const swiping = useRef(false);
  const [offsetX, setOffsetX] = useState(0);
  const [revealed, setRevealed] = useState(false);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    currentX.current = startX.current;
    swiping.current = true;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!swiping.current) return;
    currentX.current = e.touches[0].clientX;
    const dx = currentX.current - startX.current;
    // Only allow swiping left (negative), or right to close if revealed
    if (revealed) {
      const newOffset = Math.min(0, Math.max(-DELETE_BTN_WIDTH, -DELETE_BTN_WIDTH + dx));
      setOffsetX(newOffset);
    } else {
      const newOffset = Math.min(0, dx);
      setOffsetX(newOffset);
    }
  }, [revealed]);

  const onTouchEnd = useCallback(() => {
    swiping.current = false;
    if (revealed) {
      // If swiped right enough, close
      if (offsetX > -DELETE_BTN_WIDTH / 2) {
        setOffsetX(0);
        setRevealed(false);
      } else {
        setOffsetX(-DELETE_BTN_WIDTH);
      }
    } else {
      if (offsetX < -SWIPE_THRESHOLD) {
        setOffsetX(-DELETE_BTN_WIDTH);
        setRevealed(true);
        hapticMedium();
      } else {
        setOffsetX(0);
      }
    }
  }, [offsetX, revealed]);

  const close = useCallback(() => {
    setOffsetX(0);
    setRevealed(false);
  }, []);

  const handleDelete = useCallback(() => {
    close();
    onDelete?.();
  }, [onDelete, close]);

  return { offsetX, revealed, onTouchStart, onTouchMove, onTouchEnd, handleDelete, close };
}

export function ConversationRow({ item, onClick, onDelete }: ConversationRowProps) {
  const swipe = useSwipeToDelete(onDelete);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const handleClick = () => {
    if (swipe.revealed) {
      swipe.close();
      return;
    }
    onClick();
  };

  const handleGroupLeave = () => {
    swipe.close();
    setShowLeaveConfirm(true);
  };

  const confirmLeave = () => {
    setShowLeaveConfirm(false);
    onDelete?.();
  };

  if (item.type === 'dm') {
    const { partner, lastMessage, unreadCount } = item;
    const airline = getAirline(partner.airlineCode);
    const hasUnread = unreadCount > 0;

    return (
      <div className="relative overflow-hidden">
        {/* Delete button behind */}
        {onDelete && (
          <button
            onClick={swipe.handleDelete}
            className="absolute right-0 top-0 bottom-0 flex items-center justify-center bg-red-500 text-white"
            style={{ width: DELETE_BTN_WIDTH }}
          >
            <Trash2 size={20} />
          </button>
        )}
        <div
          className="relative bg-slate-900 transition-transform duration-200 ease-out"
          style={{ transform: `translateX(${swipe.offsetX}px)`, transitionDuration: swipe.offsetX === 0 || swipe.offsetX === -DELETE_BTN_WIDTH ? '200ms' : '0ms' }}
          onTouchStart={swipe.onTouchStart}
          onTouchMove={swipe.onTouchMove}
          onTouchEnd={swipe.onTouchEnd}
        >
          <button
            onClick={handleClick}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/50 active:bg-slate-700/50 transition-colors text-left"
          >
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden"
              style={{ backgroundColor: airline.color, color: airline.textOnColor }}
            >
              <AvatarImg src={partner.avatarUrl} name={partner.name} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-slate-100 truncate">{partner.name}</span>
                <span className="text-xs text-slate-500">@{partner.handle}</span>
              </div>
              <p className={`text-xs truncate mt-0.5 ${hasUnread ? 'text-slate-200 font-semibold' : 'text-slate-400'}`}>
                {lastMessage.content}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className="text-[11px] text-slate-500">{relativeTime(lastMessage.createdAt)}</span>
              {hasUnread && (
                <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-accent text-[10px] font-bold text-white px-1">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Group conversation
  const { group, members, lastMessage, unreadCount } = item;
  const hasUnread = unreadCount > 0;

  return (
    <div className="relative overflow-hidden">
      {/* Leave button behind */}
      {onDelete && (
        <button
          onClick={handleGroupLeave}
          className="absolute right-0 top-0 bottom-0 flex items-center justify-center bg-red-500 text-white gap-1"
          style={{ width: DELETE_BTN_WIDTH }}
        >
          <LogOut size={16} />
        </button>
      )}
      {/* Leave confirmation modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60" onClick={() => setShowLeaveConfirm(false)}>
          <div className="bg-slate-800 rounded-2xl p-5 mx-6 w-full max-w-xs border border-slate-700" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-100 mb-2">Leave Group?</h3>
            <p className="text-sm text-slate-400 mb-4">
              You'll be removed from this group and auto-decline any pending meetup proposals.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowLeaveConfirm(false)} className="px-4 py-2 text-sm text-slate-400 rounded-lg hover:bg-slate-700">Cancel</button>
              <button onClick={confirmLeave} className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg active:scale-95">Leave</button>
            </div>
          </div>
        </div>
      )}
      <div
        className="relative bg-slate-900 transition-transform duration-200 ease-out"
        style={{ transform: `translateX(${swipe.offsetX}px)`, transitionDuration: swipe.offsetX === 0 || swipe.offsetX === -DELETE_BTN_WIDTH ? '200ms' : '0ms' }}
        onTouchStart={swipe.onTouchStart}
        onTouchMove={swipe.onTouchMove}
        onTouchEnd={swipe.onTouchEnd}
      >
        <button
          onClick={handleClick}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/50 active:bg-slate-700/50 transition-colors text-left"
        >
          {/* Stacked avatars */}
          <div className="w-11 h-11 relative shrink-0">
            {members.slice(0, 2).map((user, i) => {
              const airline = getAirline(user.airlineCode);
              return (
                <div
                  key={user.id}
                  className="absolute w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-slate-900 overflow-hidden"
                  style={{
                    backgroundColor: airline.color,
                    color: airline.textOnColor,
                    top: i === 0 ? 0 : 'auto',
                    bottom: i === 1 ? 0 : 'auto',
                    left: i === 0 ? 0 : 'auto',
                    right: i === 1 ? 0 : 'auto',
                  }}
                >
                  <AvatarImg src={user.avatarUrl} name={user.name} />
                </div>
              );
            })}
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-slate-100 truncate block">{group.name}</span>
            <p className={`text-xs truncate mt-0.5 ${hasUnread ? 'text-slate-200 font-semibold' : 'text-slate-400'}`}>
              {lastMessage.content}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-[11px] text-slate-500">{relativeTime(lastMessage.createdAt)}</span>
            {hasUnread && (
              <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-accent text-[10px] font-bold text-white px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
        </button>
      </div>
    </div>
  );
}
